import { useParams, Link } from 'react-router-dom';
import { articles, categories, getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const catMap = Object.fromEntries(categories.map(c => [c.slug, c.name]));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-400 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-800 mb-2">文章不存在</h1>
            <p className="text-sm text-foreground-500 mb-4">你要找的文章不存在或已被移除</p>
            <Link to="/" className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find(c => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article, 3);

  return (
    <>
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: catMap[article.category] || article.category, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 lg:px-10 pb-16">
          <div className="max-w-4xl mx-auto">
            <header className="mb-8 md:mb-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium hover:bg-primary-500/20 transition-colors cursor-pointer"
                >
                  {catMap[article.category] || article.category}
                </Link>
                {article.isBreaking && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-500/15 text-accent-400 text-xs font-medium">
                    <i className="ri-flashlight-fill text-[10px]"></i>
                    头条
                  </span>
                )}
                {article.isHot && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium">
                    <i className="ri-fire-fill text-[10px]"></i>
                    热门
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-5 tracking-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-500">
                <div className="flex items-center gap-2.5">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-medium text-foreground-700">{article.author}</span>
                    <span className="text-xs text-foreground-500">51暗网特约记者</span>
                  </div>
                </div>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {new Date(article.publishedAt).toLocaleDateString('zh-CN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {(article.views / 10000).toFixed(1)}万阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line"></i>
                  {article.comments}评论
                </span>
              </div>
            </header>

            <div className="mb-8 md:mb-10 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover object-top"
                loading="eager"
              />
            </div>

            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-lg bg-background-100 border border-background-200/30 text-xs text-foreground-500"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 p-4 md:p-5 rounded-xl bg-background-100 border border-background-200/30 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                  <p className="text-xs text-foreground-500">51暗网特约记者 · 专注网红塌房黑料报道</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200/50 hover:bg-primary-500/20 text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-fill"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200/50 hover:bg-primary-500/20 text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-fill"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200/50 hover:bg-primary-500/20 text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg font-semibold text-foreground-700 mb-6 font-label tracking-wide uppercase">
                相关吃瓜
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((rel) => (
                  <ArticleCard
                    key={rel.slug}
                    slug={rel.slug}
                    title={rel.title}
                    excerpt={rel.excerpt}
                    imageUrl={rel.imageUrl}
                    category={rel.category}
                    categoryName={catMap[rel.category] || rel.category}
                    publishedAt={rel.publishedAt}
                    views={rel.views}
                    comments={rel.comments}
                    isHot={rel.isHot}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "author": {
            "@type": "Person",
            "name": article.author
          },
          "datePublished": article.publishedAt,
          "dateModified": article.publishedAt,
          "image": article.imageUrl,
          "articleSection": catMap[article.category] || article.category,
          "inLanguage": "zh-CN",
          "isPartOf": {
            "@type": "WebSite",
            "name": "51暗网",
            "url": "https://www.51darknet.com"
          },
          "publisher": {
            "@type": "Organization",
            "name": "51暗网",
            "url": "https://www.51darknet.com"
          }
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://www.51darknet.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": catMap[article.category] || article.category,
              "item": `https://www.51darknet.com/category/${article.category}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": article.title
            }
          ]
        })
      }} />
    </>
  );
}